// components/category/CategoryPricingManager.jsx - FINAL VERSION
// This version works with your existing Dropdown component
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  DocumentTextIcon,
  ClockIcon,
} from "@heroicons/react/outline";
import Button from "../../components/common/Button";
import Modal from "../../components/common/Modal";
import Dropdown from "../../components/common/Dropdown";
import Badge from "../../components/common/Badge";
import {
  setCategoryPricingForBranchThunk,
  removeCategoryPricingForBranchThunk,
  getCategoryPricingRulesThunk,
  getCategoryPricingInfoThunk,
  bulkUpdateProductsPricingThunk,
  getCategoryPricingAuditThunk,
} from "../../store/thunks/categoryThunks";
import {
  selectPricingRules,
  selectPricingRulesLoading,
} from "../../store/slices/pricingSlice";
import { getPricingRulesThunk } from "store/thunks/pricingThunks";

const CategoryPricingManager = ({ category, branches = [] }) => {
  const dispatch = useDispatch();
  const pricingRules = useSelector(selectPricingRules);
  const pricingRulesLoading = useSelector(selectPricingRulesLoading);
  const {
    pricingRules: categoryPricingRules,
    pricingAudit,
    pricingLoading,
  } = useSelector((state) => state.category);

  const [selectedBranch, setSelectedBranch] = useState("");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);

  // Store the selected objects for the Dropdown
  const [selectedBranchObj, setSelectedBranchObj] = useState(null);
  const [selectedRuleObj, setSelectedRuleObj] = useState(null);

  // Store the actual values for the API
  const [assignData, setAssignData] = useState({
    branchCode: "",
    pricingRuleId: "",
    reason: "",
    applyToProducts: false,
    onlyWithoutRules: true,
  });

  // Transform branches and rules for the Dropdown (needs _id and name)
  const branchOptions = branches.map((b) => ({
    _id: b.code, // Use code as the _id
    name: `${b.name} (${b.code})`,
  }));

  const ruleOptions = pricingRules.map((rule) => ({
    _id: rule._id,
    name: rule.name,
  }));

  // Load pricing rules and category pricing info
  useEffect(() => {
    if (category?._id) {
      dispatch(getPricingRulesThunk({ active: true }));
      dispatch(getCategoryPricingRulesThunk(category._id));
    }
  }, [dispatch, category]);

  // Handle branch selection
  const handleBranchChange = (branchOption) => {
    setSelectedBranchObj(branchOption);
    // Extract the code (which we stored as _id) from the option object
    setAssignData((prev) => ({
      ...prev,
      branchCode: branchOption ? branchOption._id : "",
    }));
  };

  // Handle pricing rule selection
  const handlePricingRuleChange = (ruleOption) => {
    setSelectedRuleObj(ruleOption);
    // Extract the _id from the option object
    setAssignData((prev) => ({
      ...prev,
      pricingRuleId: ruleOption ? ruleOption._id : "",
    }));
  };

  const handleAssignRule = () => {
    if (!assignData.branchCode || !assignData.pricingRuleId) {
      alert("Please select both branch and pricing rule");
      return;
    }

    dispatch(
      setCategoryPricingForBranchThunk({
        categoryId: category._id,
        branchCode: assignData.branchCode,
        pricingRuleId: assignData.pricingRuleId,
        reason: assignData.reason,
        applyToProducts: assignData.applyToProducts,
        onlyWithoutRules: assignData.onlyWithoutRules,
      })
    ).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        setShowAssignModal(false);
        // Reset both the objects and the data
        setSelectedBranchObj(null);
        setSelectedRuleObj(null);
        setAssignData({
          branchCode: "",
          pricingRuleId: "",
          reason: "",
          applyToProducts: false,
          onlyWithoutRules: true,
        });
        // Refresh category pricing rules
        dispatch(getCategoryPricingRulesThunk(category._id));
      }
    });
  };

  const handleRemoveRule = (branchCode) => {
    if (!window.confirm("Are you sure you want to remove this pricing rule?")) {
      return;
    }

    dispatch(
      removeCategoryPricingForBranchThunk({
        categoryId: category._id,
        branchCode,
        reason: "Manual removal from category pricing manager",
        removeFromProducts: false,
      })
    ).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        dispatch(getCategoryPricingRulesThunk(category._id));
      }
    });
  };

  const handleViewPricingInfo = (branchCode) => {
    dispatch(
      getCategoryPricingInfoThunk({
        categoryId: category._id,
        branchCode,
      })
    );
  };

  const handleBulkUpdate = (branchCode, pricingRuleId) => {
    // Set the data for bulk update
    setAssignData({
      ...assignData,
      branchCode,
      pricingRuleId,
    });

    // Also set the selected objects for display in the modal
    const branchObj = branchOptions.find((b) => b._id === branchCode);
    const ruleObj = ruleOptions.find((r) => r._id === pricingRuleId);
    setSelectedBranchObj(branchObj);
    setSelectedRuleObj(ruleObj);

    setShowBulkUpdateModal(true);
  };

  const executeBulkUpdate = () => {
    dispatch(
      bulkUpdateProductsPricingThunk({
        categoryId: category._id,
        branchCode: assignData.branchCode,
        pricingRuleId: assignData.pricingRuleId,
        onlyWithoutRules: assignData.onlyWithoutRules,
      })
    ).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        setShowBulkUpdateModal(false);
        alert(`Successfully updated ${result.payload.updated} products`);
      }
    });
  };

  const loadAuditLog = () => {
    dispatch(
      getCategoryPricingAuditThunk({
        categoryId: category._id,
        params: { limit: 50 },
      })
    );
    setShowAuditModal(true);
  };

  // Get the pricing rule name by ID
  const getRuleName = (ruleId) => {
    const rule = pricingRules.find((r) => r._id === ruleId);
    return rule?.name || "Unknown Rule";
  };

  // Reset selected objects when modal closes
  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedBranchObj(null);
    setSelectedRuleObj(null);
    setAssignData({
      branchCode: "",
      pricingRuleId: "",
      reason: "",
      applyToProducts: false,
      onlyWithoutRules: true,
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold">
            Branch-Specific Pricing Rules
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Manage pricing rules for different branches
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={loadAuditLog}
            className="flex items-center gap-1"
          >
            <ClockIcon className="w-4 h-4" />
            View Audit Log
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowAssignModal(true)}
            className="flex items-center gap-1"
          >
            <PlusIcon className="w-4 h-4" />
            Assign Rule
          </Button>
        </div>
      </div>

      {/* Category Pricing Configuration */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <span className="text-xs text-gray-500">
              Allow Product Override
            </span>
            <div className="mt-1">
              <Badge
                variant={
                  category?.pricingConfig?.allowProductOverride
                    ? "success"
                    : "secondary"
                }
              >
                {category?.pricingConfig?.allowProductOverride ? "Yes" : "No"}
              </Badge>
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-500">Inherit from Parent</span>
            <div className="mt-1">
              <Badge
                variant={
                  category?.pricingConfig?.inheritFromParent
                    ? "success"
                    : "secondary"
                }
              >
                {category?.pricingConfig?.inheritFromParent ? "Yes" : "No"}
              </Badge>
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-500">Priority</span>
            <div className="mt-1">
              <Badge variant="info">
                {category?.pricingConfig?.priority || 0}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Branch Rules Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Branch
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Pricing Rule
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Applied From
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Applied By
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {categoryPricingRules?.branchSpecificRules?.length > 0 ? (
              categoryPricingRules.branchSpecificRules.map((rule) => (
                <tr key={rule.branchCode}>
                  <td className="px-4 py-3 text-sm">
                    <Badge variant="secondary">{rule.branchCode}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div>
                      <p className="font-medium">
                        {rule.pricingRuleId?.name ||
                          getRuleName(rule.pricingRuleId)}
                      </p>
                      {rule.pricingRuleId?.description && (
                        <p className="text-xs text-gray-500">
                          {rule.pricingRuleId.description}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(rule.appliedFrom).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {rule.appliedBy?.adminName || "System"}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleViewPricingInfo(rule.branchCode)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View Pricing Info"
                      >
                        <DocumentTextIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          handleBulkUpdate(rule.branchCode, rule.pricingRuleId)
                        }
                        className="text-green-600 hover:text-green-800"
                        title="Apply to Products"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRemoveRule(rule.branchCode)}
                        className="text-red-600 hover:text-red-800"
                        title="Remove Rule"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                  No branch-specific pricing rules configured
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Assign Rule Modal */}
      <Modal
        isOpen={showAssignModal}
        closeModal={closeAssignModal}
        title="Assign Pricing Rule to Branch"
      >
        <div className="space-y-4">
          <Dropdown
            label="Select Branch"
            options={branchOptions}
            selected={selectedBranchObj}
            onChange={handleBranchChange}
            placeholder="Choose a branch"
          />

          <Dropdown
            label="Select Pricing Rule"
            options={ruleOptions}
            selected={selectedRuleObj}
            onChange={handlePricingRuleChange}
            placeholder="Choose a pricing rule"
            isOptionsLoading={pricingRulesLoading}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason/Notes
            </label>
            <textarea
              value={assignData.reason}
              onChange={(e) =>
                setAssignData({ ...assignData, reason: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={3}
              placeholder="Enter reason for this assignment..."
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={assignData.applyToProducts}
                onChange={(e) =>
                  setAssignData({
                    ...assignData,
                    applyToProducts: e.target.checked,
                  })
                }
                className="mr-2"
              />
              <span className="text-sm">
                Apply to products in this category
              </span>
            </label>

            {assignData.applyToProducts && (
              <label className="flex items-center ml-6">
                <input
                  type="checkbox"
                  checked={assignData.onlyWithoutRules}
                  onChange={(e) =>
                    setAssignData({
                      ...assignData,
                      onlyWithoutRules: e.target.checked,
                    })
                  }
                  className="mr-2"
                />
                <span className="text-sm">
                  Only update products without existing rules
                </span>
              </label>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={closeAssignModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAssignRule}
              disabled={pricingLoading}
            >
              {pricingLoading ? "Assigning..." : "Assign Rule"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Update Modal */}
      <Modal
        isOpen={showBulkUpdateModal}
        closeModal={() => setShowBulkUpdateModal(false)}
        title="Apply Pricing Rule to Products"
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              This will update all products in this category with the selected
              pricing rule for branch <strong>{assignData.branchCode}</strong>.
            </p>
          </div>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={assignData.onlyWithoutRules}
              onChange={(e) =>
                setAssignData({
                  ...assignData,
                  onlyWithoutRules: e.target.checked,
                })
              }
              className="mr-2"
            />
            <span className="text-sm">
              Only update products without existing rules
            </span>
          </label>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowBulkUpdateModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={executeBulkUpdate}
              disabled={pricingLoading}
            >
              {pricingLoading ? "Updating..." : "Update Products"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Audit Log Modal */}
      <Modal
        isOpen={showAuditModal}
        closeModal={() => setShowAuditModal(false)}
        title="Pricing Audit Log"
        size="xl"
      >
        <div className="max-h-96 overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                  Date
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                  Action
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                  Branch
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                  Rule
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                  User
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pricingAudit?.length > 0 ? (
                pricingAudit.map((log, index) => (
                  <tr key={index}>
                    <td className="px-3 py-2 text-xs">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <Badge
                        variant={
                          log.action === "RULE_ASSIGNED"
                            ? "success"
                            : log.action === "RULE_REMOVED"
                            ? "danger"
                            : "info"
                        }
                        size="xs"
                      >
                        {log.action}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {log.branchCode || "GLOBAL"}
                    </td>
                    <td className="px-3 py-2 text-xs">{log.ruleName || "-"}</td>
                    <td className="px-3 py-2 text-xs">
                      {log.appliedBy?.adminName || "System"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="px-3 py-4 text-center text-gray-500 text-sm"
                  >
                    No audit logs available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
};

export default CategoryPricingManager;
